import NumberFlow from '@number-flow/react';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import { Progress } from 'src/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { ForecastPaginatedActionsRenameSessionModal } from 'src/features/forecastPaginated/forecastPaginatedActions/ForecastPaginatedActionsRenameSessionModal';
import { ForecastPaginatedActionsWarningLastProductModal } from 'src/features/forecastPaginated/forecastPaginatedActions/ForecastPaginatedActionsWarningLastProductModal';
import { useForecastPaginatedLastAction } from 'src/features/forecastPaginated/forecastPaginatedActions/hooks/useForecastPaginatedLastAction';
import { ForecastSessionPicker } from 'src/features/forecastSessions/ForecastSessionPicker';

type ForecastPaginatedActionsProps = {
  sessionId: string;
  totalProducts: number;
  validatedNumber: number;
  sku: string;
  isDestocking?: boolean;
  validatedAt?: string | null;
  handlePrevious: () => void;
  handleNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  handleReset: () => void;
  isLoading: boolean;
};

export function ForecastPaginatedActions({
  sessionId,
  totalProducts,
  validatedNumber,
  sku,
  isDestocking,
  validatedAt,
  handlePrevious,
  handleNext,
  hasPrevious,
  hasNext,
  handleReset,
  isLoading,
}: ForecastPaginatedActionsProps) {
  const { t } = useTranslation('forecast');
  const {
    confirmLabel,
    isRenamingSession,
    isWarningModalOpen,
    nextSessionName,
    renameSessionName,
    renameModalOpen,
    requireNextSessionName,
    setNextSessionName,
    setRenameSessionName,
    handleActionClick,
    handleCancelRename,
    handleConfirmLastAction,
    handleConfirmRename,
    handleDismissWarning,
  } = useForecastPaginatedLastAction({
    hasNext,
    handleNext,
    isDestocking,
    sessionId,
    sku,
  });
  const isLastPendingProduct =
    !validatedAt && totalProducts && totalProducts - validatedNumber === 1;

  return (
    <div className='flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
      <div className='flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:gap-x-4'>
        <ForecastSessionPicker className='w-full lg:w-auto' />
        <div>
          <Button
            className='rounded-r-none border-r-[0.5px] focus:ring-0 focus:ring-offset-0'
            variant='outline'
            size='sm'
            onClick={handlePrevious}
            disabled={!hasPrevious || isLoading}
          >
            <ChevronLeft className='mr-1 h-4 w-4' />
          </Button>
          <Button
            className='rounded-l-none border-l-[0.5px] focus:ring-0 focus:ring-offset-0'
            variant='outline'
            size='sm'
            onClick={handleNext}
            disabled={!hasNext || isLoading}
          >
            <ChevronRight className='ml-1 h-4 w-4' />
          </Button>
        </div>
        <div className='flex flex-col gap-y-1 lg:min-w-48'>
          <span className='flex items-center self-end text-xs text-layout-foreground-secondary'>
            <NumberFlow
              value={validatedNumber}
              suffix={` ${t('of')} ${totalProducts}`}
            />
            <Tooltip>
              <TooltipTrigger>
                <Info className='ml-1 size-2.5' />
              </TooltipTrigger>
              <TooltipContent>
                {t('Product validation progress')}
              </TooltipContent>
            </Tooltip>
          </span>
          <Progress
            value={(validatedNumber / totalProducts) * 100}
            className='w-48'
          />
        </div>
      </div>
      <div className='flex flex-wrap items-center gap-3 lg:flex-nowrap lg:gap-x-4'>
        <Button variant='secondary' onClick={handleReset} disabled={isLoading}>
          {t('Reset')}
        </Button>
        <Button
          variant='outline'
          disabled={isLoading}
          onClick={() => {
            handleActionClick('skip', Boolean(isLastPendingProduct));
          }}
        >
          {t('Skip')}
        </Button>
        <Button
          disabled={isLoading}
          onClick={() => {
            handleActionClick('validate', Boolean(isLastPendingProduct));
          }}
        >
          {t('Validate')}
        </Button>
      </div>
      <ForecastPaginatedActionsWarningLastProductModal
        open={isWarningModalOpen}
        confirmLabel={confirmLabel}
        nextSessionName={nextSessionName}
        requireNextSessionName={requireNextSessionName}
        onNextSessionNameChange={setNextSessionName}
        handleConfirm={handleConfirmLastAction}
        handleCancel={() => {
          handleDismissWarning();
          handleReset();
        }}
      />
      <ForecastPaginatedActionsRenameSessionModal
        open={renameModalOpen}
        sessionName={renameSessionName}
        isSubmitting={isRenamingSession}
        onSessionNameChange={setRenameSessionName}
        onConfirm={handleConfirmRename}
        onCancel={() => {
          void handleCancelRename();
        }}
      />
    </div>
  );
}
